import {
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IPracticeMigration,
  ITranslationMap,
  type IDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  all$,
  doc,
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  getError,
  omitByKeys,
  resolveSequentially,
  runTransaction,
  snapshotCombineLatest,
  Timestamp,
  toNamedDocument,
  WithPrimaryKey,
  WithRef,
  type IIdentifiable,
} from '@principle-theorem/shared';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BasePatientInvoiceDestinationEntity,
  IInvoiceBuildData,
  IInvoiceUpdateData,
  IPatientInvoiceJobData,
  IPatientInvoiceMigrationData,
  PATIENT_INVOICE_DESTINATION_ENTITY,
  PATIENT_INVOICE_RESOURCE_TYPE,
  PATIENT_TRANSACTION_RESOURCE_TYPE,
} from '../../../destination/entities/patient-invoices';
import { PatientLineItemsSourceEntity } from '../../source/entities/patient-line-items';
import { PatientRefundSourceEntity } from '../../source/entities/patient-refunds';
import { PatientTransactionsSourceEntity } from '../../source/entities/patient-transactions';
import {
  PatientSourceEntity,
  PracticeWorksPatient,
} from '../../source/entities/patients';
import { PracticeWorksItemCodeMappingHandler } from '../mappings/item-codes';
import { PracticeWorksStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patient';
import { combineLatest, Observable, of } from 'rxjs';
import { TranslationMapHandler } from '../../../translation-map';
import {
  AccountCredit,
  Brand,
  BuildAccountAggregates,
  hasMergeConflicts,
  Invoice,
  Patient,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  IPatient,
  ITreatmentConfiguration,
  TransactionProvider,
  type IBasePatient,
  type IInvoice,
  type IPatientContactDetails,
  type IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import { map, switchMap } from 'rxjs/operators';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { compact, sumBy } from 'lodash';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { PatientBalanceSourceEntity } from '../../../exact/source/entities/patient-balance';
import { PatientDiscountSourceEntity } from '../../source/entities/patient-discounts';
import { PracticeWorksInvoiceBuilder } from './lib/practice-works-invoice-builder';
import { ExactItemCodeToTreatmentMappingHandler } from '../../../exact/destination/mappings/item-code-to-treatment';
import { PATIENT_DEPOSIT_RESOURCE_TYPE } from '../../../destination/entities/patient-deposits';
import { PracticeWorksItemCodeToTransactionTypeMappingHandler } from '../mappings/item-code-to-transaction-type';

export const patientInvoiceDestinationEntity =
  DestinationEntity.withMetadataDescription(
    PATIENT_INVOICE_DESTINATION_ENTITY,
    `This is structured the same as Oasis' invoice migration as there is a single table of all incoming and outgoing.`
  );

interface IPracticeWorksInvoiceJobData
  extends IPatientInvoiceJobData<PracticeWorksPatient> {
  itemCodeToPayment: WithPrimaryKey<
    ITranslationMap<object, TransactionProvider>
  >[];
  sourceItemCodesToTreatments: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[];
}

export class PatientInvoicesDestinationEntity extends BasePatientInvoiceDestinationEntity<
  PracticeWorksPatient,
  IPracticeWorksInvoiceJobData
> {
  override destinationEntity = patientInvoiceDestinationEntity;
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    lineItems: new PatientLineItemsSourceEntity(),
    refunds: new PatientRefundSourceEntity(),
    transactions: new PatientTransactionsSourceEntity(),
    discounts: new PatientDiscountSourceEntity(),
    balances: new PatientBalanceSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new PracticeWorksStafferMappingHandler(),
    itemCodes: new PracticeWorksItemCodeMappingHandler(),
    itemCodeToPayment:
      new PracticeWorksItemCodeToTransactionTypeMappingHandler(),
    itemCodeToTreatments: new ExactItemCodeToTreatmentMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPracticeWorksInvoiceJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = brand$.pipe(
      switchMap((brand) => all$(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const itemCodeToPayment$ =
      this.customMappings.itemCodeToPayment.getRecords(translationMap);
    const sourceItemCodesToTreatments$ =
      this.customMappings.itemCodeToTreatments.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      brand$,
      practitioners$,
      sourceItemCodes$,
      itemCodeToPayment$,
      sourceItemCodesToTreatments$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          patients,
          [
            staff,
            brand,
            practitioners,
            sourceItemCodes,
            itemCodeToPayment,
            sourceItemCodesToTreatments,
          ],
        ]) =>
          patients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            brand,
            practitioners,
            sourceItemCodes,
            itemCodeToPayment,
            sourceItemCodesToTreatments,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IPracticeWorksInvoiceJobData
  ): Promise<
    | IPatientInvoiceMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const practice = await Firestore.getDoc(
        migration.configuration.practices[0].ref
      );
      const patientId = jobData.sourcePatient.data.id;
      const patientRef = await translationMap.getDestination<IPatient>(
        patientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this.buildErrorResponse(
          jobData.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const patient = await Firestore.getDoc(patientRef);
      const sourceTransactions =
        await this.sourceEntities.transactions.filterRecords(
          migration,
          'responsePatientId',
          patientId
        );
      const sourceLineItems = await this.sourceEntities.lineItems.filterRecords(
        migration,
        'responsePatientId',
        patientId
      );
      const sourceRefunds = await this.sourceEntities.refunds.filterRecords(
        migration,
        'responsePatientId',
        patientId
      );
      const sourceDiscounts = await this.sourceEntities.discounts.filterRecords(
        migration,
        'responsePatientId',
        patientId
      );
      const patientBalances = await this.sourceEntities.balances.filterRecords(
        migration,
        'patientId',
        patientId
      );

      const balance = patientBalances.length
        ? sumBy(
            patientBalances,
            (patientBalance) => patientBalance.data.balance
          )
        : 0;

      const invoiceBuilder = new PracticeWorksInvoiceBuilder(
        migration,
        translationMap,
        jobData.staff,
        jobData.practitioners,
        jobData.sourceItemCodes,
        jobData.sourceItemCodesToTreatments,
        jobData.itemCodeToPayment
      );
      invoiceBuilder.buildAccountSummary({
        balance,
        sourceLineItems,
        sourceTransactions,
        sourceRefunds,
        sourceDiscounts,
      });
      // This is not as expected in other systems
      // invoiceBuilder.validateAccountSummary();

      const invoiceData = await invoiceBuilder.buildInvoices(
        patient as IBasePatient & IPatientContactDetails,
        practice
      );

      return {
        patientRef: patient.ref,
        data: invoiceData,
        createdAt: Timestamp.now(),
      };
    } catch (error) {
      return this.buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IPracticeWorksInvoiceJobData,
    migrationData: IPatientInvoiceMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const invoiceRefs = await this._upsertInvoices(
        migrationData,
        translationMap,
        migrationData.patientRef
      );

      const accountAggregateBuilder = new BuildAccountAggregates(console);
      await accountAggregateBuilder.updatePatient(
        migrationData.patientRef,
        false
      );

      return this._buildSuccessResponse(jobData.sourcePatient, invoiceRefs);
    } catch (error) {
      return this.buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    migrationData: IPatientInvoiceMigrationData
  ): Promise<IPatientInvoiceMigrationData | undefined> {
    const invoiceMergeConflicts: (IInvoiceBuildData | undefined)[] =
      await resolveSequentially(
        migrationData.data,
        async (invoiceBuildData) => {
          try {
            const invoiceRef = await translationMap.getDestination<IInvoice>(
              invoiceBuildData.invoice.uid,
              PATIENT_TRANSACTION_RESOURCE_TYPE
            );

            if (!invoiceRef) {
              return;
            }

            const existingInvoice = await Firestore.getDoc(invoiceRef);
            const invoiceMergeConflict = hasMergeConflicts(
              omitByKeys(invoiceBuildData.invoice, ['from', 'to', 'reference']),
              omitByKeys(existingInvoice, ['from', 'to', 'reference']),
              [],
              [
                {
                  key: 'transactions',
                  sortByPath: 'reference',
                },
              ]
            );

            const existingTransactions: (IIdentifiable & ITransaction)[] = [];

            const transactionMergeConflicts = await resolveSequentially(
              invoiceBuildData.transactions,
              async (transaction) => {
                try {
                  const existingTransaction = await Firestore.getDoc(
                    doc(
                      Invoice.transactionCol(existingInvoice),
                      transaction.uid
                    )
                  );

                  existingTransactions.push({
                    ...existingTransaction,
                  });

                  return hasMergeConflicts(transaction, existingTransaction);
                } catch {
                  return false;
                }
              }
            );

            const hasMergeConflict = [
              invoiceMergeConflict,
              ...transactionMergeConflicts,
            ].some((mergeConflict) => mergeConflict);

            if (hasMergeConflict) {
              return {
                ...invoiceBuildData,
                invoice: {
                  ...existingInvoice,
                  uid: invoiceBuildData.invoice.uid,
                },
                transactions: existingTransactions,
              };
            }
          } catch {
            return;
          }
        }
      );

    if (invoiceMergeConflicts.some((conflict) => conflict)) {
      return {
        ...migrationData,
        data: compact(invoiceMergeConflicts),
      };
    }
  }

  private async _upsertInvoices(
    migrationData: IPatientInvoiceMigrationData,
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>
  ): Promise<IInvoiceUpdateData[]> {
    // TODO: delete this when happy with results
    // await runTransaction(async (firestoreTransaction) => {
    //   const invoices = await Firestore.getDocs(
    //     Patient.invoiceCol({
    //       ref: patientRef,
    //     })
    //   );

    //   const filteredInvoices = invoices.filter((invoice) =>
    //     FirestoreMigrate.isMigratedDocument(invoice)
    //   );

    //   await resolveSequentially(filteredInvoices, async (invoice) => {
    //     if (invoice.deleted) {
    //       return;
    //     }
    //     const transactions = await Firestore.getDocs(
    //       Invoice.transactionCol(invoice)
    //     );
    //     const filteredTransactions = transactions.filter((transaction) =>
    //       FirestoreMigrate.isMigratedDocument(transaction)
    //     );
    //     await resolveSequentially(filteredTransactions, async (transaction) => {
    //       if (transaction.deleted) {
    //         return;
    //       }
    //       await FirestoreMigrate.deleteDoc(
    //         transaction.ref,
    //         firestoreTransaction
    //       );
    //     });
    //     await FirestoreMigrate.deleteDoc(invoice.ref, firestoreTransaction);
    //   });

    //   const credits = await Firestore.getDocs(
    //     AccountCredit.col({ ref: patientRef })
    //   );
    //   await resolveSequentially(credits, async (credit) => {
    //     if (credit.deleted || !FirestoreMigrate.isMigratedDocument(credit)) {
    //       return;
    //     }
    //     await FirestoreMigrate.deleteDoc(credit.ref, firestoreTransaction);
    //   });
    // });

    const invoiceData = await runTransaction(async (firestoreTransaction) =>
      resolveSequentially(migrationData.data, async (invoiceBuildData) => {
        const invoiceDestinationRef =
          await translationMap.getDestination<IInvoice>(
            invoiceBuildData.invoice.uid,
            PATIENT_INVOICE_RESOURCE_TYPE
          );

        const invoiceRef = await FirestoreMigrate.upsertDoc(
          Patient.invoiceCol({
            ref: patientRef,
          }),
          { ...invoiceBuildData.invoice, deleted: false },
          invoiceDestinationRef?.id,
          firestoreTransaction
        );

        if (!invoiceDestinationRef) {
          await translationMap.upsert({
            sourceIdentifier: invoiceBuildData.invoice.uid,
            destinationIdentifier: invoiceRef,
            resourceType: PATIENT_INVOICE_RESOURCE_TYPE,
          });
        }

        const transactionRefs = await resolveSequentially(
          invoiceBuildData.transactions,
          async (transaction) => {
            const transactionDestinationRef =
              await translationMap.getDestination<ITransaction>(
                invoiceBuildData.invoice.uid,
                PATIENT_TRANSACTION_RESOURCE_TYPE
              );

            const transactionRef = await FirestoreMigrate.upsertDoc(
              Invoice.transactionCol({
                ref: invoiceRef,
              }),
              { ...transaction, deleted: false },
              transaction.uid,
              firestoreTransaction
            );

            if (!transactionDestinationRef) {
              await translationMap.upsert({
                sourceIdentifier: transaction.uid,
                destinationIdentifier: transactionRef,
                resourceType: PATIENT_TRANSACTION_RESOURCE_TYPE,
              });
            }

            return transactionRef;
          }
        );

        const resolvedInvoice = {
          ...invoiceBuildData.invoice,
          ref: invoiceRef,
        };

        const accountCredits = Invoice.getDueCredits(resolvedInvoice, []);

        const accountCreditRefs = await resolveSequentially(
          accountCredits,
          async (accountCredit) => {
            const accountCreditDestinationRef =
              await translationMap.getDestination<ITransaction>(
                invoiceBuildData.invoice.uid,
                PATIENT_DEPOSIT_RESOURCE_TYPE
              );

            const accountCreditRef = await FirestoreMigrate.upsertDoc(
              AccountCredit.col({
                ref: patientRef,
              }),
              { ...accountCredit, deleted: false },
              accountCredit.depositUid,
              firestoreTransaction
            );

            if (!accountCreditDestinationRef) {
              await translationMap.upsert({
                sourceIdentifier: accountCredit.depositUid,
                destinationIdentifier: accountCreditRef,
                resourceType: PATIENT_DEPOSIT_RESOURCE_TYPE,
              });
            }

            return accountCreditRef;
          }
        );

        return {
          invoiceRef,
          transactionRefs,
          accountCreditRefs,
        };
      })
    );

    return compact(invoiceData);
  }
}
